import { useMemo, useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { FaArrowLeft, FaGlobe } from "react-icons/fa"
import {
  Box,
  HStack,
  IconButton,
  Popover,
  Portal,
  Spinner,
  Stack,
  Table,
} from "@chakra-ui/react"
import { Section, Text } from "@/components"
import { usePitchbookInvestor } from "@/hooks"
import { PbInvestorDealData } from "@/types"

const formatCurrency = (amount: number | null, currency: string | null) => {
  if (amount == null) return "—"
  const curr = currency || "USD"
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: curr,
    maximumFractionDigits: 0,
  }).format(amount)
}

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return "—"
  return dateStr
}

const parseInvestorTypes = (raw: string | null): { description: string; primary: boolean }[] => {
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw)
    return parsed.map((item: { type: { description: string }; primary: boolean }) => ({
      description: item.type.description,
      primary: item.primary,
    }))
  } catch {
    return [{ description: raw, primary: false }]
  }
}

const parseTradeAssociations = (raw: string | null): string[] => {
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw)
    return parsed.map((item: { name: string }) => item.name)
  } catch {
    return [raw]
  }
}

const SynopsisPopover = ({ text }: { text: string }) => {
  const [open, setOpen] = useState(false)
  return (
    <Popover.Root open={open} onOpenChange={(e) => setOpen(e.open)}>
      <Popover.Trigger asChild>
        <Text
          fontSize="xs"
          lineClamp={2}
          cursor="default"
          _hover={{ color: "blue.600" }}
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
        >
          {text}
        </Text>
      </Popover.Trigger>
      <Portal>
        <Popover.Positioner>
          <Popover.Content maxW="400px" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
            <Popover.Arrow />
            <Popover.Body>
              <Text fontSize="xs" lineHeight="tall">{text}</Text>
            </Popover.Body>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  )
}

const HeaderStat = ({ label, value }: { label: string; value: string | null | undefined }) => (
  <Stack gap={0}>
    <Text fontSize="xs" color="whiteAlpha.700" fontWeight="medium">
      {label}
    </Text>
    <Text fontSize="sm" color="white" fontWeight="semibold">
      {value || "—"}
    </Text>
  </Stack>
)

const InfoCard = ({ label, value }: { label: string; value: string | null | undefined }) => (
  <Box bg="gray.50" borderRadius="md" p={3} minW="160px">
    <Text fontSize="xs" color="gray.500" fontWeight="medium">
      {label}
    </Text>
    <Text fontSize="sm" fontWeight="semibold" mt={1}>
      {value || "—"}
    </Text>
  </Box>
)

const InvestorDealRow = ({ deal, navigate }: { deal: PbInvestorDealData; navigate: ReturnType<typeof useNavigate> }) => {
  const participation = deal.investorParticipation[0]
  const isLead = deal.investorParticipation.some((p) => p.leadSoleInvestor)
  const roles = [...new Set(deal.investorParticipation.map((p) => p.role))].join(", ")

  return (
    <Table.Row _hover={{ bg: "gray.50" }}>
      <Table.Cell>{formatDate(deal.dealDate)}</Table.Cell>
      <Table.Cell>
        <Text
          fontSize="xs"
          fontWeight="medium"
          color="blue.700"
          cursor="pointer"
          _hover={{ textDecoration: "underline" }}
          onClick={() => navigate(`/core/pitchbook/company/${deal.companyId}`)}
        >
          {deal.companyName || "Unknown Company"}
        </Text>
      </Table.Cell>
      <Table.Cell>{deal.dealType2Description || deal.dealType1Description || "—"}</Table.Cell>
      <Table.Cell>{formatCurrency(deal.dealSizeAmount, deal.dealSizeCurrency)}</Table.Cell>
      <Table.Cell>{formatCurrency(participation?.investmentAmountAmount ?? null, participation?.investmentAmountCurrency ?? null)}</Table.Cell>
      <Table.Cell>
        <Text fontSize="xs">{roles}{isLead ? " (Lead)" : ""}</Text>
      </Table.Cell>
      <Table.Cell>{deal.vcRound || "—"}</Table.Cell>
      <Table.Cell>{deal.dealStatusDescription || "—"}</Table.Cell>
      <Table.Cell maxW="300px">
        {deal.dealSynopsis ? (
          <SynopsisPopover text={deal.dealSynopsis} />
        ) : (
          <Text fontSize="xs">—</Text>
        )}
      </Table.Cell>
    </Table.Row>
  )
}

export const PitchbookInvestorComponent = () => {
  const params = useParams<{ pbInvestorId: string }>()
  const navigate = useNavigate()
  const { data, isLoading } = usePitchbookInvestor(params.pbInvestorId)
  const [roleFilter, setRoleFilter] = useState<string | null>(null)

  const allRoles = useMemo(() => {
    if (!data) return []
    const roles = new Set<string>()
    for (const deal of data.deals) {
      for (const p of deal.investorParticipation) {
        if (p.role) roles.add(p.role)
      }
    }
    return [...roles].sort()
  }, [data])

  const filteredDeals = useMemo(() => {
    if (!data) return []
    if (!roleFilter) return data.deals
    return data.deals.filter((deal) =>
      deal.investorParticipation.some((p) => p.role === roleFilter)
    )
  }, [data, roleFilter])

  if (isLoading) {
    return (
      <Section h="100vh" alignItems="center" justifyContent="center">
        <Spinner size="lg" />
      </Section>
    )
  }

  if (!data) {
    return (
      <Section h="100vh">
        <Text>No PitchBook investor data available.</Text>
      </Section>
    )
  }

  const { investorProfile, deals } = data
  const hqLocation = [investorProfile?.hqCity, investorProfile?.hqStateProvince, investorProfile?.hqCountry]
    .filter(Boolean)
    .join(", ")

  return (
    <Section overflowY="auto" h="100vh" p={0} gap={0}>
      {/* Back Button */}
      <Box px={4} py={2}>
        <IconButton
          aria-label="Go back"
          variant="ghost"
          size="sm"
          onClick={() => navigate(-1)}
        >
          <FaArrowLeft />
        </IconButton>
      </Box>

      {/* Header */}
      <Box bg="#1C5180" color="white" px={8} py={6}>
        <HStack justify="space-between" align="flex-start">
          <Stack gap={1}>
            <Text fontSize="2xl" fontWeight="bold" color="white">
              {investorProfile?.formalName || investorProfile?.alsoKnownAs || "Investor"}
            </Text>
            {investorProfile?.legalName && (
              <Text fontSize="sm" color="whiteAlpha.800">
                {investorProfile.legalName}
              </Text>
            )}
            {investorProfile?.formerlyKnownAs && (
              <Text fontSize="xs" color="whiteAlpha.600">
                Formerly: {investorProfile.formerlyKnownAs}
              </Text>
            )}
          </Stack>
          <HStack gap={3}>
            {investorProfile?.website && (
              <IconButton
                aria-label="Website"
                variant="ghost"
                size="sm"
                color="white"
                _hover={{ bg: "whiteAlpha.200" }}
                onClick={() => window.open(investorProfile.website!.startsWith("http") ? investorProfile.website! : `https://${investorProfile.website}`, "_blank")}
              >
                <FaGlobe />
              </IconButton>
            )}
          </HStack>
        </HStack>

        <HStack gap={8} mt={4} flexWrap="wrap">
          <HeaderStat label="Year Founded" value={investorProfile?.yearFounded?.toString()} />
          <HeaderStat label="HQ Location" value={hqLocation || null} />
          <HeaderStat label="Investor Status" value={investorProfile?.investorStatusDescription} />
          <Stack gap={0}>
            <Text fontSize="xs" color="whiteAlpha.700" fontWeight="medium">Investor Types</Text>
            <HStack gap={1} flexWrap="wrap">
              {parseInvestorTypes(investorProfile?.investorTypes ?? null).length > 0
                ? parseInvestorTypes(investorProfile?.investorTypes ?? null).map((t) => (
                    <Box key={t.description} bg={t.primary ? "whiteAlpha.300" : "whiteAlpha.100"} px={1.5} py={0.5} borderRadius="sm">
                      <Text fontSize="xs" color="white" fontWeight={t.primary ? "bold" : "normal"}>{t.description}</Text>
                    </Box>
                  ))
                : <Text fontSize="sm" color="white" fontWeight="semibold">—</Text>
              }
            </HStack>
          </Stack>
          <HeaderStat label="Investment Professionals" value={investorProfile?.countInvestmentProfessionals?.toLocaleString()} />
        </HStack>
      </Box>

      {/* Body */}
      <Stack px={8} py={6} gap={5}>
        {/* Description */}
        {(investorProfile?.descriptionBrief || investorProfile?.descriptionFull) && (
          <Stack gap={1}>
            <Text fontSize="md" fontWeight="bold">
              Overview
            </Text>
            <Text fontSize="sm" color="gray.700" lineHeight="tall">
              {investorProfile.descriptionBrief || investorProfile.descriptionFull}
            </Text>
          </Stack>
        )}

        {/* Info Cards */}
        <HStack gap={3} flexWrap="wrap">
          <InfoCard
            label="AUM"
            value={
              investorProfile?.aumAmount != null
                ? `${formatCurrency(investorProfile.aumAmount, investorProfile.aumCurrency)}${investorProfile.aumEstimated ? " (est.)" : ""}`
                : null
            }
          />
          <InfoCard
            label="Dry Powder"
            value={
              investorProfile?.dryPowderAmount != null
                ? `${formatCurrency(investorProfile.dryPowderAmount, investorProfile.dryPowderCurrency)}${investorProfile.dryPowderEstimated ? " (est.)" : ""}`
                : null
            }
          />
          <InfoCard label="Investor Status" value={investorProfile?.investorStatusDescription} />
          <Box bg="gray.50" borderRadius="md" p={3} minW="160px">
            <Text fontSize="xs" color="gray.500" fontWeight="medium">Trade Associations</Text>
            <HStack gap={1} flexWrap="wrap" mt={1}>
              {parseTradeAssociations(investorProfile?.tradeAssociations ?? null).length > 0
                ? parseTradeAssociations(investorProfile?.tradeAssociations ?? null).map((name) => (
                    <Box key={name} bg="gray.200" px={1.5} py={0.5} borderRadius="sm">
                      <Text fontSize="xs" fontWeight="medium">{name}</Text>
                    </Box>
                  ))
                : <Text fontSize="sm" fontWeight="semibold">—</Text>
              }
            </HStack>
          </Box>
        </HStack>

        {/* Deals Table */}
        {deals.length > 0 && (
          <Stack gap={2} mt={2}>
            <HStack justify="space-between" align="center">
              <Text fontSize="md" fontWeight="bold">
                Deal History ({filteredDeals.length}{roleFilter ? ` of ${deals.length}` : ""} deals)
              </Text>
              {allRoles.length > 1 && (
                <HStack gap={1}>
                  <Box
                    as="button"
                    px={2}
                    py={1}
                    borderRadius="full"
                    fontSize="xs"
                    fontWeight="medium"
                    bg={!roleFilter ? "#1C5180" : "gray.100"}
                    color={!roleFilter ? "white" : "gray.600"}
                    cursor="pointer"
                    onClick={() => setRoleFilter(null)}
                  >
                    All
                  </Box>
                  {allRoles.map((role) => (
                    <Box
                      as="button"
                      key={role}
                      px={2}
                      py={1}
                      borderRadius="full"
                      fontSize="xs"
                      fontWeight="medium"
                      bg={roleFilter === role ? "#1C5180" : "gray.100"}
                      color={roleFilter === role ? "white" : "gray.600"}
                      cursor="pointer"
                      _hover={{ bg: roleFilter === role ? "#1C5180" : "gray.200" }}
                      onClick={() => setRoleFilter(role)}
                    >
                      {role}
                    </Box>
                  ))}
                </HStack>
              )}
            </HStack>
            <Box borderWidth="1px" borderRadius="md" overflow="auto">
              <Table.Root size="sm" variant="outline">
                <Table.Header>
                  <Table.Row bg="gray.50">
                    <Table.ColumnHeader>Date</Table.ColumnHeader>
                    <Table.ColumnHeader>Company</Table.ColumnHeader>
                    <Table.ColumnHeader>Deal Type</Table.ColumnHeader>
                    <Table.ColumnHeader>Deal Size</Table.ColumnHeader>
                    <Table.ColumnHeader>Investment</Table.ColumnHeader>
                    <Table.ColumnHeader>Role</Table.ColumnHeader>
                    <Table.ColumnHeader>VC Round</Table.ColumnHeader>
                    <Table.ColumnHeader>Status</Table.ColumnHeader>
                    <Table.ColumnHeader>Synopsis</Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {filteredDeals.map((deal) => (
                    <InvestorDealRow key={deal.id} deal={deal} navigate={navigate} />
                  ))}
                </Table.Body>
              </Table.Root>
            </Box>
          </Stack>
        )}
      </Stack>
    </Section>
  )
}
